import queryString from 'query-string';
import { Dispatch } from 'redux';
import { AddressSavePayload } from 'src/api/customer/addresses';
import { PageableQueryParams } from 'src/entities/basic-types/Pageable';
import { OrganizationCustomerEnableDto } from 'src/entities/organization/OrganizationCustomerEnableDto';
import OrgCustomerPremiumLevels from 'src/entities/organization/OrgCustomerPremiumLevels';
import { SCHEMA_ADDRESS_SHAPE } from 'src/schemas/MyAddressBook';
import {
    SCHEMA_ORGANIZATION_CUSTOMER_PREMIUM_LEVEL_SHAPE,
    SCHEMA_ORGANIZATION_CUSTOMER_REFERRAL_PARTNER_SHAPE,
} from 'src/schemas/Organization';
import { enqueueSnackbarAction } from 'src/store/notifications/actions';
import callApi, { CallApiResult, saveFile } from '.';

export type AccountID = 'current' | number;

/* Fetch Registered Events */

// TODO new endpoint?
export const fetchRegisteredEvents = (id: AccountID, partnerId: number): Promise<CallApiResult> => {
    const endpoint = `/academy/v2/${id}/events/${partnerId}`;
    return callApi(endpoint);
};

/* Fetch Video List */

// TODO new endpoint?
export const fetchVideoList = (id: AccountID, partnerId: number): Promise<CallApiResult> => {
    const endpoint = `/academy/v2/${id}/videoList/${partnerId}`;
    return callApi(endpoint);
};

/* Fetch Organization Customer Orders */

// TODO new endpoint?
export const fetchOrganizationCustomerOrders = (
    id: AccountID = 'current',
    partnerId: number,
    pageable: PageableQueryParams = {}
): Promise<CallApiResult> => {
    let query = queryString.stringify(pageable, { skipEmpty: true, skipNull: true });
    if (query !== '') query = '?' + query;

    const endpoint = `/academy/v2/${id}/customerorders/${partnerId}${query}`;
    return callApi(endpoint);
};

/* Fetch Organization Customer History */

export const fetchOrganizationCustomerHistory = (
    id: AccountID = 'current',
    customerId: number,
    pageable: PageableQueryParams = {}
): Promise<CallApiResult> => {
    let query = queryString.stringify(pageable, { skipEmpty: true, skipNull: true });
    if (query !== '') query = '?' + query;

    const endpoint = `/api/officeProfiOrder/${id}/customer/${customerId}/history${query}`;
    return callApi(endpoint);
};

/* Fetch Favorites */

// TODO remove
export const fetchOrganizationFavorites = (id: AccountID = 'current'): Promise<CallApiResult> => {
    const endpoint = `/academy/v2/${id}/partnerFavorites`;
    return callApi(endpoint);
};

/* Fetch statsitic (optionally range-scoped: curr/prev pairs follow the range, totals stay lifetime) */
export const fetchOrganizationStatistic = (id: AccountID = 'current', range?: string): Promise<CallApiResult> => {
    let query = queryString.stringify({ range }, { skipEmpty: true, skipNull: true });
    if (query !== '') query = '?' + query;

    const endpoint = `/api/organization/${id}/statistic${query}`;
    return callApi(endpoint);
};

/* Fetch lead-unlock conditions + access */
export const fetchOrganizationLeadUnlock = (id: AccountID = 'current'): Promise<CallApiResult> => {
    const endpoint = `/api/organization/${id}/lead-unlock`;
    return callApi(endpoint);
};

/* Fetch the lead-unlock events one condition counts (the card's pop-up list) */

export type LeadConditionListId = 'appointments' | 'firstorders' | 'specialprice' | 'referrer';

export interface LeadConditionEntry {
    name: string;
    customerId: number | null;
    nameListEntryId: string | null;
    /** Vienna date 'YYYY-MM-DD' the event counts from. */
    date: string;
    /** Order number (firstorders only). */
    reference: string | null;
}

export const fetchLeadConditionEntries = (
    id: AccountID,
    conditionId: LeadConditionListId
): Promise<CallApiResult<LeadConditionEntry[]>> => {
    return callApi<LeadConditionEntry[]>(`/api/organization/${id}/lead-unlock/${conditionId}/entries`);
};

/* Fetch the lifetime leaderboards (structure revenue + held appointments) */
export const fetchOrganizationRanking = (id: AccountID = 'current'): Promise<CallApiResult> => {
    return callApi(`/api/organization/${id}/ranking`);
};

/* Fetch the caller's places per past month (tab Entwicklung), newest first */

export interface OrganizationMonthlyRank {
    /** 'YYYY-MM' */
    month: string;
    revenueRank: number | null;
    revenue: number | null;
    appointmentsRank: number | null;
}

export interface OrganizationMonthlyRanking {
    months: OrganizationMonthlyRank[];
    hasMore: boolean;
}

export const fetchOrganizationMonthlyRanking = (
    id: AccountID,
    page: number,
    size: number
): Promise<CallApiResult<OrganizationMonthlyRanking>> => {
    const query = queryString.stringify({ page, size });
    return callApi<OrganizationMonthlyRanking>(`/api/organization/${id}/ranking/monthly?${query}`);
};

/* Fetch the structure revenue per month (Umsatzübersicht), oldest first, the last = the current month */

export interface OrganizationMonthlyRevenue {
    /** 'YYYY-MM' */
    month: string;
    amount: number;
}

export const fetchOrganizationMonthlyRevenue = (
    id: AccountID,
    months: number
): Promise<CallApiResult<OrganizationMonthlyRevenue[]>> => {
    return callApi<OrganizationMonthlyRevenue[]>(`/api/organization/${id}/revenue/monthly?months=${months}`);
};

/* ── Tags (Phase C): the partner's reusable tag library + per-contact assignment ── */

export interface OrgTag {
    id: number;
    label: string;
    color?: string;
}

export const fetchOrgTags = (id: AccountID = 'current'): Promise<CallApiResult<OrgTag[]>> => {
    return callApi<OrgTag[]>(`/api/organization/${id}/tags`);
};

/** R36-7: a partner-role catalogue entry the contacts list can be filtered by. */
export interface ContactRoleOption {
    roleId: string | null;
    slug: string | null;
    name: string | null;
}

/** The Kunde role(s) plus the roles the partner may grant (Settings › Partnerrollen › darf vergeben). */
export const fetchContactRoleOptions = (id: AccountID = 'current'): Promise<CallApiResult<ContactRoleOption[]>> => {
    return callApi<ContactRoleOption[]>(`/api/organization/${id}/contactRoleOptions`);
};

export const createOrgTag = (
    label: string,
    color: string | undefined,
    id: AccountID = 'current'
): Promise<CallApiResult<OrgTag>> => {
    return callApi<OrgTag>(`/api/organization/${id}/tags`, { method: 'POST', data: { label, color } });
};

export const updateOrgTag = (
    tagId: number,
    label: string,
    color: string | undefined,
    id: AccountID = 'current'
): Promise<CallApiResult<OrgTag>> => {
    return callApi<OrgTag>(`/api/organization/${id}/tags/${tagId}`, {
        method: 'PUT',
        data: { label, color },
    });
};

export const deleteOrgTag = (tagId: number, id: AccountID = 'current'): Promise<CallApiResult> => {
    return callApi(`/api/organization/${id}/tags/${tagId}`, { method: 'DELETE' });
};

export const fetchContactTags = (
    nameListEntryId: string,
    id: AccountID = 'current'
): Promise<CallApiResult<OrgTag[]>> => {
    return callApi<OrgTag[]>(`/api/organization/${id}/contact/${nameListEntryId}/tags`);
};

export const setContactTags = (
    nameListEntryId: string,
    tagIds: number[],
    id: AccountID = 'current'
): Promise<CallApiResult<OrgTag[]>> => {
    return callApi<OrgTag[]>(`/api/organization/${id}/contact/${nameListEntryId}/tags`, {
        method: 'PUT',
        data: { tagIds },
    });
};

/* Fetch appointments / Termine (paged, server-filtered by timeframe + type) */
export const fetchOrganizationAppointments = (
    id: AccountID = 'current',
    timeframe?: string,
    type?: string,
    page?: number,
    pageSize?: number,
    sortBy?: string,
    sortDirection?: string
): Promise<CallApiResult> => {
    let query = queryString.stringify(
        { timeframe, type, page, pageSize, sortBy, sortDirection },
        { skipEmpty: true, skipNull: true }
    );
    if (query !== '') query = '?' + query;

    const endpoint = `/api/organization/${id}/appointments${query}`;
    return callApi(endpoint);
};

/* Fetch organisation-wide orders (ordering-api, paged) */
export const fetchOrganizationOrders = (
    id: AccountID = 'current',
    params: Record<string, string | number> = {}
): Promise<CallApiResult> => {
    let query = queryString.stringify(params, { skipEmpty: true, skipNull: true });
    if (query !== '') query = '?' + query;

    const endpoint = `/api/officeProfiOrder/${id}/orders${query}`;
    return callApi(endpoint);
};

/* Fetch organisation-scoped offers (ordering-api, paged) */
export const fetchOrganizationOffers = (
    id: AccountID = 'current',
    params: Record<string, string | number> = {}
): Promise<CallApiResult> => {
    let query = queryString.stringify(params, { skipEmpty: true, skipNull: true });
    if (query !== '') query = '?' + query;

    const endpoint = `/api/officeProfiOrder/${id}/offers/page${query}`;
    return callApi(endpoint);
};

/* Fetch one contact's Office-Profi offers (ordering-api, paged, server-filtered by customerId) */
export const fetchCustomerOffers = (
    id: AccountID,
    customerId: number,
    params: Record<string, string | number> = {}
): Promise<CallApiResult> => {
    let query = queryString.stringify(params, { skipEmpty: true, skipNull: true });
    if (query !== '') query = '?' + query;

    return callApi(`/api/officeProfiOrder/${id}/customer/${customerId}/offers${query}`);
};

/* Fetch one contact's subscriptions (ordering-api, paged; response carries the abosTotal/abosActive/abosMonthlyAmount totals) */
export const fetchCustomerSubscriptions = (
    id: AccountID,
    customerId: number,
    params: Record<string, string | number> = {}
): Promise<CallApiResult> => {
    let query = queryString.stringify(params, { skipEmpty: true, skipNull: true });
    if (query !== '') query = '?' + query;

    return callApi(`/api/officeProfiOrder/${id}/customer/${customerId}/subscriptions${query}`);
};

/* Fetch one contact's order stats (ordering-api; totals across ALL of the contact's orders, not a page) */

export interface CustomerOrderStats {
    orderCount: number;
    totalRevenue: number;
    averageBasket: number;
    currencyIso: string;
}

export const fetchCustomerOrderStats = (
    id: AccountID,
    customerId: number
): Promise<CallApiResult<CustomerOrderStats>> => {
    return callApi<CustomerOrderStats>(`/api/officeProfiOrder/${id}/customer/${customerId}/orders/stats`);
};

/* Fetch overview */

export const fetchOrganizationOverview = (
    id: AccountID = 'current',
    pageable: PageableQueryParams = {}
): Promise<CallApiResult> => {
    let query = queryString.stringify(pageable, { skipEmpty: true, skipNull: true });
    if (query !== '') query = '?' + query;

    const endpoint = `/api/organization/${id}/overview${query}`;
    return callApi(endpoint);
};

/* Fetch contacts (server-side filtered, sorted and paginated) */

export const fetchOrganizationContacts = (
    id: AccountID = 'current',
    pageable: PageableQueryParams = {},
    filterParams: Record<string, string | boolean> = {}
): Promise<CallApiResult> => {
    let query = queryString.stringify({ ...pageable, ...filterParams }, { skipEmpty: true, skipNull: true });
    if (query !== '') query = '?' + query;

    const endpoint = `/api/organization/${id}/contacts${query}`;
    return callApi(endpoint);
};

/* Registration confirm-flow: link a matched customer to a name-list entry / reverse a link */

export const linkOrganizationContactCustomer = (
    id: AccountID = 'current',
    nameListEntryId: string,
    customerId: number
): Promise<CallApiResult> => {
    const endpoint = `/api/organization/${id}/contacts/${nameListEntryId}/link-customer?customerId=${customerId}`;
    return callApi(endpoint, { method: 'POST' });
};

export const unlinkOrganizationContactCustomer = (
    id: AccountID = 'current',
    nameListEntryId: string
): Promise<CallApiResult> => {
    const endpoint = `/api/organization/${id}/contacts/${nameListEntryId}/customer-link`;
    return callApi(endpoint, { method: 'DELETE' });
};

/* Fetch one contact's dated steps to the registration (Historie), in time order */

export type ContactMilestoneType = 'CONTACT_CREATED' | 'REGISTRATION_SENT' | 'REGISTERED';

export interface ContactMilestone {
    type: ContactMilestoneType;
    /** ISO instant */
    at: string;
}

export const fetchContactMilestones = (
    id: AccountID,
    nameListEntryId: string
): Promise<CallApiResult<ContactMilestone[]>> => {
    return callApi<ContactMilestone[]>(`/api/organization/${id}/contacts/${nameListEntryId}/milestones`);
};

/* Fetch Organization To-Dos (server-assembled, paginated/sorted/filtered) */
export const fetchOrganizationTodos = (
    id: AccountID = 'current',
    pageable: PageableQueryParams = {},
    category: string = 'all'
): Promise<CallApiResult> => {
    let query = queryString.stringify({ ...pageable, category }, { skipEmpty: true, skipNull: true });
    if (query !== '') query = '?' + query;

    const endpoint = `/api/organization/${id}/todos${query}`;
    return callApi(endpoint);
};

/* Fetch Organization Customers */
export const fetchOrganizationCustomers = (
    id: AccountID = 'current',
    pageable: PageableQueryParams = {}
): Promise<CallApiResult> => {
    let query = queryString.stringify(pageable, { skipEmpty: true, skipNull: true });
    if (query !== '') query = '?' + query;

    const endpoint = `/api/organization/${id}/customers${query}`;
    return callApi(endpoint);
};

/* Fetch Organization Sis Partners */
export const fetchOrganizationSisPartners = (
    id: AccountID = 'current',
    pageable: PageableQueryParams = {}
): Promise<CallApiResult> => {
    let query = queryString.stringify(pageable, { skipEmpty: true, skipNull: true });
    if (query !== '') query = '?' + query;

    const endpoint = `/api/organization/${id}/sisPartners${query}`;
    return callApi(endpoint);
};

/* Fetch Organization Licensors */
export const fetchOrganizationLicensors = (
    id: AccountID = 'current',
    pageable: PageableQueryParams = {}
): Promise<CallApiResult> => {
    let query = queryString.stringify(pageable, { skipEmpty: true, skipNull: true });
    if (query !== '') query = '?' + query;

    const endpoint = `/api/organization/${id}/licensors${query}`;
    return callApi(endpoint);
};

/* Fetch Organization Placements */
export const fetchOrganizationPlacements = (
    id: AccountID = 'current',
    pageable: PageableQueryParams = {}
): Promise<CallApiResult> => {
    let query = queryString.stringify(pageable, { skipEmpty: true, skipNull: true });
    if (query !== '') query = '?' + query;

    const endpoint = `/api/organization/${id}/placements${query}`;
    return callApi(endpoint);
};

/* Fetch Organization Office Profis */
export const fetchOrganizationOfficeProfis = (
    id: AccountID = 'current',
    pageable: PageableQueryParams = {}
): Promise<CallApiResult> => {
    let query = queryString.stringify(pageable, { skipEmpty: true, skipNull: true });
    if (query !== '') query = '?' + query;

    const endpoint = `/api/organization/${id}/officeprofis${query}`;
    return callApi(endpoint);
};

/* Fetch Organization Customer Appliances */
export const fetchOrganizationCustomerAppliances = (
    sourceId: AccountID = 'current',
    targetId: AccountID
): Promise<CallApiResult> => {
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/appliances`;
    return callApi(endpoint);
};

/* Fetch Organization Customer Data */
export const fetchOrganizationCustomerData = (
    sourceId: AccountID = 'current',
    targetId: AccountID
): Promise<CallApiResult> => {
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/data`;
    return callApi(endpoint);
};

export const patchOrganizationCustomerData = (
    sourceId: AccountID = 'current',
    targetId: AccountID,
    data: SCHEMA_ORGANIZATION_CUSTOMER_PREMIUM_LEVEL_SHAPE
): Promise<CallApiResult> => {
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/data`;
    return callApi(endpoint, {
        method: 'PATCH',
        data,
    });
};

/* Customer mail-address management — org-scoped twins of the /api/customer mailAddress endpoints. */
export const addOrganizationCustomerMailAddress = (
    sourceId: AccountID = 'current',
    targetId: AccountID,
    mailAddress: string
): Promise<CallApiResult> => {
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/mailAddress`;
    return callApi(endpoint, { method: 'POST', data: { mailAddress } });
};

export const assignOrganizationCustomerMailAddressAction = (
    sourceId: AccountID = 'current',
    targetId: AccountID,
    action: 'account' | 'newsletter',
    mailAddressId: string
): Promise<CallApiResult> => {
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/mailAddress/${action}/${mailAddressId}`;
    return callApi(endpoint, { method: 'POST' });
};

export const deleteOrganizationCustomerMailAddress = (
    sourceId: AccountID = 'current',
    targetId: AccountID,
    mailAddressId: string
): Promise<CallApiResult> => {
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/mailAddress/${mailAddressId}`;
    return callApi(endpoint, { method: 'DELETE' });
};

export const resendOrganizationCustomerMailConfirmation = (
    sourceId: AccountID = 'current',
    targetId: AccountID,
    mailAddressId: string
): Promise<CallApiResult> => {
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/mailAddress/${mailAddressId}/resendConfirmationToken`;
    return callApi(endpoint, { method: 'POST' });
};

/* Customer phone-number management — org-scoped twins of the /api/customer phoneNumber endpoints. */
export const addOrganizationCustomerPhoneNumber = (
    sourceId: AccountID = 'current',
    targetId: AccountID,
    phoneNumber: string
): Promise<CallApiResult> => {
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/phoneNumber`;
    return callApi(endpoint, { method: 'POST', data: { phoneNumber } });
};

export const assignOrganizationCustomerPhoneNumberAction = (
    sourceId: AccountID = 'current',
    targetId: AccountID,
    action: 'account',
    phoneNumberId: string
): Promise<CallApiResult> => {
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/phoneNumber/${action}/${phoneNumberId}`;
    return callApi(endpoint, { method: 'POST' });
};

export const deleteOrganizationCustomerPhoneNumber = (
    sourceId: AccountID = 'current',
    targetId: AccountID,
    phoneNumberId: string
): Promise<CallApiResult> => {
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/phoneNumber/${phoneNumberId}`;
    return callApi(endpoint, { method: 'DELETE' });
};

/* Fetch Organization Customer Addresses */
export const fetchOrganizationCustomerAddresses = (
    sourceId: AccountID = 'current',
    targetId: AccountID
): Promise<CallApiResult> => {
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/addresses`;
    return callApi(endpoint);
};

/* Create Organization Customer Address — org-scoped twin of the customer-self submitAddressForm POST. */
export const createOrganizationCustomerAddress = (
    sourceId: AccountID = 'current',
    targetId: AccountID,
    data: SCHEMA_ADDRESS_SHAPE | AddressSavePayload
): Promise<CallApiResult> => {
    const preparedData = {
        ...data,
        additionalInvoiceData: data.additionalInvoiceData.filter(d => d.value != null && d.value.length > 0),
    };
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/addresses`;
    return callApi(endpoint, { method: 'POST', data: preparedData });
};

/* Fetch Organization Customer Payment Methods — read-only catalog of the contact's available
   payment-method types (UserPaymentResponseDto), not stored instrument detail. */
export interface CustomerPaymentMethod {
    id: number;
    normalizedName: string;
    localizedName: string;
    applicableForPartialPayment?: boolean;
    needExternalAuth?: boolean;
}

export const fetchOrganizationCustomerPaymentMethods = (
    sourceId: AccountID = 'current',
    targetId: AccountID
): Promise<CallApiResult<CustomerPaymentMethod[]>> => {
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/paymentmethods`;
    return callApi<CustomerPaymentMethod[]>(endpoint);
};

/* Fetch Organization Customer Recommendations — the customers this contact has recommended
   (Customer.refId == targetId), with each one's invoiced-order figures plus aggregate stats.
   Amounts are JSON numbers; dates are ISO strings (formatted on the FE). */
export interface CustomerRecommendation {
    customerId: number;
    customerNumber: string;
    name: string;
    company?: string | null;
    recommendedAt: string;
    lastOrderDate?: string | null;
    lastOrderAmount?: number | null;
    totalRevenue: number;
    orderCount: number;
}

export interface CustomerRecommendationStats {
    total: number;
    withOrder: number;
    totalRevenue: number;
}

export interface CustomerRecommendationsResponse {
    stats: CustomerRecommendationStats;
    recommendations: CustomerRecommendation[];
}

export const fetchOrganizationCustomerRecommendations = (
    sourceId: AccountID = 'current',
    targetId: number
): Promise<CallApiResult<CustomerRecommendationsResponse>> => {
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/recommendations`;
    return callApi<CustomerRecommendationsResponse>(endpoint);
};

/* Fetch Organization Customer document files — flat list of the contact's stored document files
   (the /files endpoint), distinct from the regulatory state view at /documents. */
export interface CustomerFile {
    id: number;
    name: string;
    fileName: string;
    contentType: string;
    uploadedOn: string;
    size: number;
    downloadUrl?: string | null;
}

export const fetchOrganizationCustomerFiles = (
    sourceId: AccountID = 'current',
    targetId: number
): Promise<CallApiResult<CustomerFile[]>> => {
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/files`;
    return callApi<CustomerFile[]>(endpoint);
};

/* Download a customer document by its (resource-service) download URL → browser save; error snackbar
   on failure. Mirrors downloadOfferPdf. */
export const downloadCustomerDocument = (downloadUrl: string, fileName: string, dispatch: Dispatch): void => {
    callApi<Blob>(downloadUrl, { method: 'GET', responseType: 'blob' })
        .then(({ error, response }) => {
            if (error) {
                dispatch(
                    enqueueSnackbarAction({
                        message: error.message,
                        options: { variant: 'error', key: 'document-download-error' },
                    })
                );
                return;
            }
            if (response) {
                saveFile(response.data, fileName);
            }
        })
        .catch(console.error);
};

/* Fetch Organization Customer Landingpages */
export const fetchOrganizationCustomerLandingpages = (
    sourceId: AccountID = 'current',
    targetId: AccountID
): Promise<CallApiResult> => {
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/landingpages`;
    return callApi(endpoint);
};

/* Fetch Organization Customer Referral Partner */
export const fetchOrganizationCustomerReferralPartner = (
    sourceId: AccountID = 'current',
    targetId: AccountID
): Promise<CallApiResult> => {
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/referralPartner`;
    return callApi(endpoint);
};

export const postOrganizationCustomerReferralPartner = (
    sourceId: AccountID = 'current',
    targetId: AccountID,
    data: SCHEMA_ORGANIZATION_CUSTOMER_REFERRAL_PARTNER_SHAPE
): Promise<CallApiResult> => {
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/referralPartner`;
    return callApi(endpoint, {
        method: 'POST',
        data,
    });
};

/** R42-1: Freigaben › Vermittler › Freigabe zurücknehmen — closes the own open release. */
export const withdrawOrganizationCustomerReferralPartner = (
    sourceId: AccountID = 'current',
    targetId: AccountID
): Promise<CallApiResult> =>
    callApi(`/api/organization/${sourceId}/customer/${targetId}/referralPartner`, { method: 'DELETE' });

/** R42-5: the Sonderpreis per article group, the rebate levels, the product special prices, the lock. */
export const fetchOrganizationCustomerSpecialPrices = (
    sourceId: AccountID = 'current',
    targetId: AccountID
): Promise<CallApiResult> => callApi(`/api/organization/${sourceId}/customer/${targetId}/specialPrices`);

/** R28-16: what releasing `roleId` for the contact would cost (the Rolle-ändern preview). */
export const fetchOrganizationRoleReleasePreview = (
    sourceId: AccountID = 'current',
    targetId: AccountID,
    roleId: string
): Promise<CallApiResult> => {
    return callApi(`/api/organization/${sourceId}/customer/${targetId}/roleReleasePreview`, { params: { roleId } });
};

/* Fetch Organization Customer Enables */
export const fetchOrganizationCustomerEnables = (
    sourceId: AccountID = 'current',
    targetId: AccountID
): Promise<CallApiResult> => {
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/enables`;
    return callApi(endpoint);
};

export const postOrganizationCustomerEnables = (
    sourceId: AccountID = 'current',
    targetId: AccountID,
    data: OrganizationCustomerEnableDto[]
): Promise<CallApiResult> => {
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/enables`;
    return callApi(endpoint, {
        method: 'POST',
        data: data.map(dto => ({
            state: dto.state,
            group: dto.group,
            type: dto.type,
            createdAt: null,
            enabledUntil: null,
        })),
    });
};

export const postOrganizationCustomerLandingpageEnable = (
    sourceId: AccountID = 'current',
    targetId: AccountID
): Promise<CallApiResult> => {
    const endpoint = `/api/organization/${sourceId}/customer/${targetId}/landingpages/enable`;
    return callApi(endpoint, {
        method: 'POST',
    });
};

/* Insert/Change Customer Premium Level */
export const applyCustomerPremiumLevel = (customerPremiumLevels: OrgCustomerPremiumLevels): Promise<CallApiResult> => {
    const endpoint = '/api/organization/customerPremiumLevel';
    return callApi(endpoint, { method: 'PUT', data: customerPremiumLevels });
};
